(() => {
  if (window.communityCarouselScriptLoaded) return;
  window.communityCarouselScriptLoaded = true;
  const SELECTOR = '[data-community-carousel]';

  class CommunityCarousel {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-community-viewport]');
      this.track = root.querySelector('[data-community-track]');
      this.previous = root.querySelector('[data-community-previous]');
      this.next = root.querySelector('[data-community-next]');
      this.loop = root.dataset.loop === 'true';
      this.autoplay = root.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.speed = Number(root.dataset.autoplaySpeed) || 30;
      this.pauseReasons = new Set(['viewport']);
      if (document.hidden) this.pauseReasons.add('document');
      this.controller = new AbortController();
      this.index = 0;
      this.trackIndex = 0;
      this.animating = false;
      this.dragging = false;
      this.resizeTimer = null;
      this.transitionTimer = null;
      this.bind();
      this.refresh();
    }

    get slides() { return Array.from(this.track.children).filter(slide => !slide.hasAttribute('data-community-clone')); }
    get step() { const slide = this.track.querySelector('[data-community-slide]'); return slide ? slide.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap || 0) : 0; }

    bind() {
      const signal = this.controller.signal;
      this.previous?.addEventListener('click', () => this.move(-1, true), { signal });
      this.next?.addEventListener('click', () => this.move(1, true), { signal });
      this.viewport.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.move(-1, true); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.move(1, true); }
      }, { signal });
      this.viewport.addEventListener('pointerdown', event => this.pointerDown(event), { signal });
      this.viewport.addEventListener('pointermove', event => this.pointerMove(event), { signal });
      this.viewport.addEventListener('pointerup', event => this.pointerEnd(event), { signal });
      this.viewport.addEventListener('pointercancel', event => this.pointerEnd(event), { signal });
      this.track.addEventListener('transitionend', event => { if (event.target === this.track && event.propertyName === 'transform') this.finish(); }, { signal });
      this.root.addEventListener('focusin', event => { if (event.target.matches?.(':focus-visible')) this.pause('focus'); }, { signal });
      this.root.addEventListener('focusout', event => { if (!this.root.contains(event.relatedTarget)) this.resume('focus'); }, { signal });
      document.addEventListener('visibilitychange', () => document.hidden ? this.pause('document') : this.resume('document'), { signal });
      window.addEventListener('resize', () => { clearTimeout(this.resizeTimer); this.resizeTimer = setTimeout(() => this.refresh(this.index), 120); }, { signal });
      this.observer = new IntersectionObserver(entries => {
        entries[0]?.isIntersecting ? this.resume('viewport') : this.pause('viewport');
      }, { threshold: 0.1 });
      this.observer.observe(this.root);
    }

    clone(slide) {
      const clone = slide.cloneNode(true);
      clone.setAttribute('data-community-clone', ''); clone.setAttribute('aria-hidden', 'true'); clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
      clone.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach(node => node.removeAttribute('data-shopify-editor-block'));
      return clone;
    }

    refresh(index = 0) {
      this.stopAutoplay(); clearTimeout(this.transitionTimer);
      this.track.querySelectorAll('[data-community-clone]').forEach(clone => clone.remove());
      const slides = this.slides; this.index = Math.min(index, Math.max(slides.length - 1, 0));
      this.root.querySelector('.community-carousel__component')?.classList.toggle('has-single-slide', slides.length <= 1);
      if (!slides.length) return;
      this.cloneCount = this.loop && slides.length > 1 ? Math.min(slides.length, Math.ceil(this.viewport.clientWidth / Math.max(slides[0].getBoundingClientRect().width, 1)) + 1) : 0;
      if (this.cloneCount) {
        slides.slice(-this.cloneCount).reverse().forEach(slide => this.track.insertBefore(this.clone(slide), this.track.firstChild));
        slides.slice(0, this.cloneCount).forEach(slide => this.track.append(this.clone(slide)));
      }
      this.trackIndex = this.cloneCount + this.index; this.position(false); this.update(); this.startAutoplay();
    }

    position(animate, offset = 0) {
      this.track.classList.toggle('is-animating', animate);
      this.currentOffset = (this.trackIndex * this.step) - offset;
      this.track.style.transform = `translate3d(${-this.currentOffset}px,0,0)`;
    }
    snapToNearest() {
      const length = this.slides.length;
      if (!length || !this.step) return;
      this.trackIndex = Math.round(this.currentOffset / this.step);
      this.index = ((this.trackIndex - this.cloneCount) % length + length) % length;
      this.trackIndex = this.cloneCount + this.index;
      this.position(false);
      this.update();
    }
    move(direction, manual = false) {
      const slides = this.slides; if (this.animating || slides.length <= 1) return;
      if (!this.loop && (this.index + direction < 0 || this.index + direction >= slides.length)) return;
      if (manual) { this.pauseTemporarily(); this.snapToNearest(); } else this.stopAutoplay();
      this.animating = true; this.index = (this.index + direction + slides.length) % slides.length; this.trackIndex += direction;
      this.position(true); this.update(); this.transitionTimer = setTimeout(() => this.finish(), 700);
    }
    goTo(index) {
      const slides = this.slides; if (!slides.length) return; this.stopAutoplay();
      this.index = Math.max(0, Math.min(index, slides.length - 1)); this.trackIndex = this.cloneCount + this.index; this.position(false); this.update(); this.startAutoplay();
    }
    finish() {
      clearTimeout(this.transitionTimer); const length = this.slides.length;
      if (this.loop && length) { this.trackIndex = this.cloneCount + this.index; this.position(false); }
      this.animating = false; this.startAutoplay();
    }
    update() {
      const slides = this.slides;
      slides.forEach((slide, i) => slide.toggleAttribute('data-community-active', i === this.index));
      if (!this.loop) { if (this.previous) this.previous.disabled = this.index === 0; if (this.next) this.next.disabled = this.index === slides.length - 1; }
    }
    pointerDown(event) {
      if (this.animating || this.slides.length <= 1 || event.button > 0) return;
      this.pause('interaction'); clearTimeout(this.resumeTimer); this.snapToNearest(); this.dragging = true; this.axis = null; this.startX = this.currentX = event.clientX; this.startY = event.clientY; this.track.classList.remove('is-animating'); this.viewport.setPointerCapture?.(event.pointerId);
    }
    pointerMove(event) {
      if (!this.dragging) return; const x = event.clientX - this.startX, y = event.clientY - this.startY;
      if (!this.axis && (Math.abs(x) > 6 || Math.abs(y) > 6)) this.axis = Math.abs(x) > Math.abs(y) ? 'x' : 'y';
      if (this.axis !== 'x') return; event.preventDefault(); this.currentX = event.clientX; this.position(false, x);
    }
    pointerEnd(event) {
      if (!this.dragging) return; this.dragging = false; this.viewport.releasePointerCapture?.(event.pointerId);
      const distance = this.currentX - this.startX;
      const direction = distance < 0 ? 1 : -1;
      const blocked = !this.loop && ((direction < 0 && this.index === 0) || (direction > 0 && this.index === this.slides.length - 1));
      if (this.axis === 'x' && !blocked && Math.abs(distance) >= Math.min(60, this.viewport.clientWidth * .12)) this.move(direction, true);
      else { this.animating = true; this.position(true); this.transitionTimer = setTimeout(() => this.finish(), 700); this.resumeTimer = setTimeout(() => this.resume('interaction'), 3000); }
    }
    pause(reason) { this.pauseReasons.add(reason); this.stopAutoplay(); }
    resume(reason) { this.pauseReasons.delete(reason); this.startAutoplay(); }
    pauseTemporarily() {
      this.pause('interaction'); clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => this.resume('interaction'), 3000);
    }
    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplay || this.pauseReasons.size || this.slides.length <= 1) return;
      if (!this.loop) {
        this.timer = setTimeout(() => this.move(this.index === this.slides.length - 1 ? -1 : 1), 2500);
        return;
      }
      this.lastFrame = 0;
      const animate = timestamp => {
        if (!this.lastFrame) this.lastFrame = timestamp;
        const elapsed = Math.min(timestamp - this.lastFrame, 50);
        this.lastFrame = timestamp;
        this.currentOffset += this.speed * elapsed / 1000;
        const cycleEnd = (this.cloneCount + this.slides.length) * this.step;
        if (this.currentOffset >= cycleEnd) this.currentOffset -= this.slides.length * this.step;
        this.track.classList.remove('is-animating');
        this.track.style.transform = `translate3d(${-this.currentOffset}px,0,0)`;
        const visibleIndex = Math.floor((this.currentOffset - this.cloneCount * this.step) / this.step);
        this.index = ((visibleIndex % this.slides.length) + this.slides.length) % this.slides.length;
        this.update();
        this.frame = requestAnimationFrame(animate);
      };
      this.frame = requestAnimationFrame(animate);
    }
    stopAutoplay() { clearTimeout(this.timer); this.timer = null; cancelAnimationFrame(this.frame); this.frame = null; this.lastFrame = 0; }
    destroy() { this.stopAutoplay(); clearTimeout(this.resumeTimer); clearTimeout(this.resizeTimer); clearTimeout(this.transitionTimer); this.observer?.disconnect(); this.controller.abort(); this.track.querySelectorAll('[data-community-clone]').forEach(clone => clone.remove()); this.track.style.removeProperty('transform'); delete this.root.communityCarousel; }
  }

  const initialize = (scope = document) => {
    const roots = scope.matches?.(SELECTOR) ? [scope] : Array.from(scope.querySelectorAll(SELECTOR));
    roots.forEach(root => { root.communityCarousel?.destroy(); root.communityCarousel = new CommunityCarousel(root); });
  };
  if (!window.communityCarouselEventsBound) {
    window.communityCarouselEventsBound = true;
    document.addEventListener('shopify:section:load', event => initialize(event.target));
    document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll(SELECTOR).forEach(root => root.communityCarousel?.destroy()));
    document.addEventListener('shopify:block:select', event => {
      const root = event.target.closest(SELECTOR); if (!root) return;
      const slide = event.target.closest('[data-community-slide]'); const index = root.communityCarousel?.slides.indexOf(slide); if (index >= 0) root.communityCarousel.goTo(index);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize()); else initialize();
})();
